import { gql } from 'graphql-request';
import { getGraphQLQueryClient } from './query-client';
import {
  JudgePosition,
  Nullable,
  TournamentStatus,
  TournamentType,
  UserWithToken,
  WithData,
} from '@ygoargentina/sdk/api/api.types';

// Public events de un main event (YCS y similares). Espejo a mano de
// apps/api/src/schemas/PublicEvent.graphql.

export type PublicEventMode = 'ON_DEMAND' | 'SCHEDULED';

export type PublicEventTypePrize = {
  id: number;
  positionFrom: number;
  positionTo: number;
  prize: string;
  minPlayers: number | null;
  /** Prize Wall Tickets del puesto. */
  tickets: number;
};

export type PublicEventType = {
  id: number;
  mainEventId: number;
  name: string;
  mode: PublicEventMode;
  formatId: number | null;
  tournamentType: TournamentType;
  playersToFire: number | null;
  minPlayers: number;
  maxPlayers: number | null;
  rounds: number | null;
  notes: string | null;
  sortOrder: number;
  /** Puntos de Public Event por jugar al menos una ronda. */
  participationPoints: number;
  /** Puntos extra para el ganador. */
  winnerPoints: number;
  /** Prize Wall Tickets por jugar al menos una ronda. */
  participationTickets: number;
  /** La entrada, como texto: "2 Booster Packs (USD 12)", "Gratis". */
  entry: string | null;
  prizes: PublicEventTypePrize[];
};

export type PublicEventPerson = { userId: number; name: string; cossyId: string | null };

export type PublicEventRound = {
  round: number;
  startedAt: string | null;
  endedAt: string | null;
};

export type PublicEvent = {
  id: number;
  title: string;
  typeId: number;
  typeName: string;
  number: number;
  mode: PublicEventMode;
  status: TournamentStatus;
  dateFrom: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  durationSeconds: number | null;
  currentRound: number | null;
  playerCount: number;
  judge: PublicEventPerson | null;
  suggestedJudge: PublicEventPerson | null;
  winner: PublicEventPerson | null;
  prizesDeliveredAt: string | null;
  /** Los inscriptos de un evento que todavía no arrancó; vacío una vez en juego. */
  enrolled: PublicEventPerson[];
  /** La entrada del tipo en el catálogo. */
  entry: string | null;
  rounds: PublicEventRound[];
};

export type PublicEventQueuePlayer = {
  entryId: number;
  userId: number;
  name: string;
  cossyId: string | null;
  createdAt: string;
};

export type PublicEventQueue = {
  typeId: number;
  typeName: string;
  playersToFire: number;
  waiting: PublicEventQueuePlayer[];
};

export type PublicEventJudgeLoad = {
  userId: number;
  name: string;
  position: JudgePosition;
  description: string | null;
  /** En pausa: no se lo sugiere para el próximo evento. */
  paused: boolean;
  running: number;
  total: number;
  finishedCount: number;
};

export type PublicEventsBoard = {
  mainEvent: { id: number; title: string };
  isLeader: boolean;
  types: PublicEventType[];
  queues: PublicEventQueue[];
  events: PublicEvent[];
  judges: PublicEventJudgeLoad[];
  averageDurationSeconds: number | null;
};

export type PublicEventPrizeInput = {
  positionFrom: number;
  positionTo: number;
  prize: string;
  minPlayers?: number | null;
  tickets?: number | null;
};

export type PublicEventTypeInput = {
  id?: number | null;
  mainEventId: number;
  name: string;
  mode: PublicEventMode;
  formatId?: number | null;
  tournamentType: TournamentType;
  playersToFire?: number | null;
  minPlayers: number;
  maxPlayers?: number | null;
  rounds?: number | null;
  notes?: string | null;
  sortOrder?: number | null;
  participationPoints?: number | null;
  winnerPoints?: number | null;
  participationTickets?: number | null;
  entry?: string | null;
  prizes: PublicEventPrizeInput[];
};

export type PublicEventPointsRow = {
  rank: number;
  userId: number;
  name: string;
  cossyId: string | null;
  points: number;
  events: number;
  wins: number;
  tickets: number;
  ticketsDelivered: number;
  ticketsPending: number;
};

export type PublicEventsPoints = {
  mainEvent: { id: number; title: string };
  playoffSpots: number;
  rows: PublicEventPointsRow[];
};

export type PublicEventPlayerEvent = {
  id: number;
  title: string;
  typeName: string;
  status: TournamentStatus;
  startedAt: string | null;
  finishedAt: string | null;
  position: number | null;
  points: number;
  prize: string | null;
  tickets: number;
  prizesDeliveredAt: string | null;
};

export type PublicEventPlayerSheet = {
  mainEvent: { id: number; title: string };
  player: PublicEventPerson;
  points: number;
  wins: number;
  rank: number | null;
  tickets: number;
  ticketsDelivered: number;
  ticketsPending: number;
  playoffSpots: number;
  events: PublicEventPlayerEvent[];
  queued: { typeName: string; createdAt: string }[];
};

export type PublicEventJudgeSheet = {
  mainEvent: { id: number; title: string };
  judge: PublicEventPerson;
  position: JudgePosition | null;
  description: string | null;
  events: PublicEvent[];
  averageDurationSeconds: number | null;
};

export type PublicEventPlayerInput = {
  cossyId?: string | null;
  name: string;
  lastName?: string | null;
};

const TYPE_FIELDS = gql`
  fragment PublicEventTypeFields on PublicEventType {
    id
    mainEventId
    name
    mode
    formatId
    tournamentType
    playersToFire
    minPlayers
    maxPlayers
    rounds
    notes
    sortOrder
    participationPoints
    winnerPoints
    participationTickets
    entry
    prizes {
      id
      positionFrom
      positionTo
      prize
      minPlayers
      tickets
    }
  }
`;

const EVENT_FIELDS = gql`
  fragment PublicEventFields on PublicEvent {
    id
    title
    typeId
    typeName
    number
    mode
    status
    dateFrom
    startedAt
    finishedAt
    durationSeconds
    currentRound
    playerCount
    judge {
      userId
      name
      cossyId
    }
    suggestedJudge {
      userId
      name
      cossyId
    }
    winner {
      userId
      name
      cossyId
    }
    prizesDeliveredAt
    enrolled {
      userId
      name
      cossyId
    }
    entry
    rounds {
      round
      startedAt
      endedAt
    }
  }
`;

export const getPublicEventsBoard = (user: Nullable<UserWithToken>, mainEventId: number) =>
  getGraphQLQueryClient(user).request<WithData<PublicEventsBoard>>(
    gql`
      ${TYPE_FIELDS}
      ${EVENT_FIELDS}
      query publicEventsBoard($mainEventId: Int!) {
        data: publicEventsBoard(mainEventId: $mainEventId) {
          mainEvent {
            id
            title
          }
          isLeader
          types {
            ...PublicEventTypeFields
          }
          queues {
            typeId
            typeName
            playersToFire
            waiting {
              entryId
              userId
              name
              cossyId
              createdAt
            }
          }
          events {
            ...PublicEventFields
          }
          judges {
            userId
            name
            position
            description
            paused
            running
            total
            finishedCount
          }
          averageDurationSeconds
        }
      }
    `,
    { mainEventId }
  );

export type PublicEventRoundMatch = {
  table: number;
  player1: string | null;
  player2: string | null;
  winner: string | null;
  resultType: 'WIN' | 'DRAW' | 'DOUBLE_LOSS' | null;
  /** Cuándo empezó a jugarse (lo marca el juez); null si no se marcó. */
  startedAt: string | null;
  reportedAt: string | null;
};

export type PublicEventRoundDetail = {
  round: number;
  startedAt: string | null;
  endedAt: string | null;
  durationSeconds: number | null;
  matches: PublicEventRoundMatch[];
};

/** Rondas de un public event con sus mesas y la hora de cada resultado. */
export const getPublicEventRounds = (user: Nullable<UserWithToken>, tournamentId: number) =>
  getGraphQLQueryClient(user).request<WithData<PublicEventRoundDetail[]>>(
    gql`
      query publicEventRounds($tournamentId: Int!) {
        data: publicEventRounds(tournamentId: $tournamentId) {
          round
          startedAt
          endedAt
          durationSeconds
          matches {
            table
            player1
            player2
            winner
            resultType
            startedAt
            reportedAt
          }
        }
      }
    `,
    { tournamentId }
  );

export type PublicEventResultRow = {
  userId: number;
  name: string;
  cossyId: string | null;
  /** Puesto; en la vista previa de eliminación directa, una franja. 0 si no jugó. */
  positionFrom: number;
  positionTo: number;
  /** Sigue en juego (vista previa de eliminación directa). */
  alive: boolean;
  prize: string | null;
  tickets: number;
  points: number;
};

export type PublicEventResult = {
  status: TournamentStatus;
  /** Vista previa: el evento no terminó. */
  provisional: boolean;
  rows: PublicEventResultRow[];
};

/** Resultado por jugador de un public event; vista previa si sigue en juego. */
export const getPublicEventResult = (user: Nullable<UserWithToken>, tournamentId: number) =>
  getGraphQLQueryClient(user).request<WithData<PublicEventResult>>(
    gql`
      query publicEventResult($tournamentId: Int!) {
        data: publicEventResult(tournamentId: $tournamentId) {
          status
          provisional
          rows {
            userId
            name
            cossyId
            positionFrom
            positionTo
            alive
            prize
            tickets
            points
          }
        }
      }
    `,
    { tournamentId }
  );

/** Tabla de puntos de Public Event del main event. */
export const getPublicEventsPoints = (user: Nullable<UserWithToken>, mainEventId: number) =>
  getGraphQLQueryClient(user).request<WithData<PublicEventsPoints>>(
    gql`
      query publicEventsPoints($mainEventId: Int!) {
        data: publicEventsPoints(mainEventId: $mainEventId) {
          mainEvent {
            id
            title
          }
          playoffSpots
          rows {
            rank
            userId
            name
            cossyId
            points
            events
            wins
            tickets
            ticketsDelivered
            ticketsPending
          }
        }
      }
    `,
    { mainEventId }
  );

/** Ficha de un jugador en los publics del main event. */
export const getPublicEventsPlayer = (
  user: Nullable<UserWithToken>,
  mainEventId: number,
  userId: number
) =>
  getGraphQLQueryClient(user).request<WithData<PublicEventPlayerSheet>>(
    gql`
      query publicEventsPlayer($mainEventId: Int!, $userId: Int!) {
        data: publicEventsPlayer(mainEventId: $mainEventId, userId: $userId) {
          mainEvent {
            id
            title
          }
          player {
            userId
            name
            cossyId
          }
          points
          wins
          rank
          tickets
          ticketsDelivered
          ticketsPending
          playoffSpots
          events {
            id
            title
            typeName
            status
            startedAt
            finishedAt
            position
            points
            prize
            tickets
            prizesDeliveredAt
          }
          queued {
            typeName
            createdAt
          }
        }
      }
    `,
    { mainEventId, userId }
  );

/** Ficha de un juez en los publics del main event. */
export const getPublicEventsJudge = (
  user: Nullable<UserWithToken>,
  mainEventId: number,
  userId: number
) =>
  getGraphQLQueryClient(user).request<WithData<PublicEventJudgeSheet>>(
    gql`
      ${EVENT_FIELDS}
      query publicEventsJudge($mainEventId: Int!, $userId: Int!) {
        data: publicEventsJudge(mainEventId: $mainEventId, userId: $userId) {
          mainEvent {
            id
            title
          }
          judge {
            userId
            name
            cossyId
          }
          position
          description
          events {
            ...PublicEventFields
          }
          averageDurationSeconds
        }
      }
    `,
    { mainEventId, userId }
  );

export const getMyPublicEvents = (user: Nullable<UserWithToken>) =>
  getGraphQLQueryClient(user).request<WithData<PublicEvent[]>>(
    gql`
      ${EVENT_FIELDS}
      query myPublicEvents {
        data: myPublicEvents {
          ...PublicEventFields
        }
      }
    `
  );

/** xlsx en base64 (hojas Eventos y Rondas). */
export const getPublicEventsExport = (user: Nullable<UserWithToken>, mainEventId: number) =>
  getGraphQLQueryClient(user).request<WithData<string>>(
    gql`
      query publicEventsExport($mainEventId: Int!) {
        data: publicEventsExport(mainEventId: $mainEventId)
      }
    `,
    { mainEventId }
  );

export const upsertPublicEventType = (user: UserWithToken, input: PublicEventTypeInput) =>
  getGraphQLQueryClient(user).request<WithData<PublicEventType>>(
    gql`
      ${TYPE_FIELDS}
      mutation upsertPublicEventType($input: PublicEventTypeInput!) {
        data: upsertPublicEventType(input: $input) {
          ...PublicEventTypeFields
        }
      }
    `,
    { input }
  );

export const deletePublicEventType = (user: UserWithToken, id: number) =>
  getGraphQLQueryClient(user).request<WithData<boolean>>(
    gql`
      mutation deletePublicEventType($id: Int!) {
        data: deletePublicEventType(id: $id)
      }
    `,
    { id }
  );

export const clonePublicEventCatalog = (
  user: UserWithToken,
  fromMainEventId: number,
  toMainEventId: number
) =>
  getGraphQLQueryClient(user).request<WithData<PublicEventType[]>>(
    gql`
      ${TYPE_FIELDS}
      mutation clonePublicEventCatalog($fromMainEventId: Int!, $toMainEventId: Int!) {
        data: clonePublicEventCatalog(
          fromMainEventId: $fromMainEventId
          toMainEventId: $toMainEventId
        ) {
          ...PublicEventTypeFields
        }
      }
    `,
    { fromMainEventId, toMainEventId }
  );

export const enqueuePublicEventPlayer = (
  user: UserWithToken,
  typeId: number,
  player: PublicEventPlayerInput
) =>
  getGraphQLQueryClient(user).request<
    WithData<{ entryId: number; firedTournamentId: number | null }>
  >(
    gql`
      mutation enqueuePublicEventPlayer($typeId: Int!, $player: PublicEventPlayerInput!) {
        data: enqueuePublicEventPlayer(typeId: $typeId, player: $player) {
          entryId
          firedTournamentId
        }
      }
    `,
    { typeId, player }
  );

export const removeFromPublicEventQueue = (user: UserWithToken, entryId: number) =>
  getGraphQLQueryClient(user).request<WithData<boolean>>(
    gql`
      mutation removeFromPublicEventQueue($entryId: Int!) {
        data: removeFromPublicEventQueue(entryId: $entryId)
      }
    `,
    { entryId }
  );

/** "Disparar ya": crea el evento con los que haya en la cola. */
export const firePublicEvent = (user: UserWithToken, typeId: number) =>
  getGraphQLQueryClient(user).request<WithData<number | null>>(
    gql`
      mutation firePublicEvent($typeId: Int!) {
        data: firePublicEvent(typeId: $typeId)
      }
    `,
    { typeId }
  );

export const createScheduledPublicEvent = (
  user: UserWithToken,
  typeId: number,
  dateFrom: string
) =>
  getGraphQLQueryClient(user).request<WithData<{ id: number; publicEventNumber: number }>>(
    gql`
      mutation createScheduledPublicEvent($typeId: Int!, $dateFrom: DateTime!) {
        data: createScheduledPublicEvent(typeId: $typeId, dateFrom: $dateFrom) {
          id
          publicEventNumber
        }
      }
    `,
    { typeId, dateFrom }
  );

export const enrollPublicEventPlayer = (
  user: UserWithToken,
  tournamentId: number,
  player: PublicEventPlayerInput
) =>
  getGraphQLQueryClient(user).request<WithData<boolean>>(
    gql`
      mutation enrollPublicEventPlayer($tournamentId: Int!, $player: PublicEventPlayerInput!) {
        data: enrollPublicEventPlayer(tournamentId: $tournamentId, player: $player)
      }
    `,
    { tournamentId, player }
  );

/** Un main event con publics en cuyo roster está el juez, con su disponibilidad. */
export type PublicEventRoster = { mainEventId: number; title: string; paused: boolean };

export const getMyPublicEventRosters = (user: Nullable<UserWithToken>) =>
  getGraphQLQueryClient(user).request<WithData<PublicEventRoster[]>>(
    gql`
      query myPublicEventRosters {
        data: myPublicEventRosters {
          mainEventId
          title
          paused
        }
      }
    `
  );

/** Reportar una mesa de un public (winnerId -1 = doble derrota); corre la llave. */
export const reportPublicEventMatch = (user: UserWithToken, matchId: number, winnerId: number) =>
  getGraphQLQueryClient(user).request<WithData<boolean>>(
    gql`
      mutation reportPublicEventMatch($matchId: Int!, $winnerId: Int!) {
        data: reportPublicEventMatch(matchId: $matchId, winnerId: $winnerId)
      }
    `,
    { matchId, winnerId }
  );

/** Corregir el resultado de una mesa ya cargada (winnerId -1 = doble derrota). */
export const correctPublicEventMatch = (user: UserWithToken, matchId: number, winnerId: number) =>
  getGraphQLQueryClient(user).request<WithData<boolean>>(
    gql`
      mutation correctPublicEventMatch($matchId: Int!, $winnerId: Int!) {
        data: correctPublicEventMatch(matchId: $matchId, winnerId: $winnerId)
      }
    `,
    { matchId, winnerId }
  );

/** Marcar (o desmarcar) que una mesa empezó a jugarse. */
export const setMatchStarted = (user: UserWithToken, matchId: number, started: boolean) =>
  getGraphQLQueryClient(user).request<WithData<boolean>>(
    gql`
      mutation setMatchStarted($matchId: Int!, $started: Boolean!) {
        data: setMatchStarted(matchId: $matchId, started: $started)
      }
    `,
    { matchId, started }
  );

/** Pausar o reanudar a un juez del roster: él mismo, o el líder a cualquiera. */
export const setPublicEventJudgePaused = (
  user: UserWithToken,
  mainEventId: number,
  userId: number,
  paused: boolean
) =>
  getGraphQLQueryClient(user).request<WithData<boolean>>(
    gql`
      mutation setPublicEventJudgePaused($mainEventId: Int!, $userId: Int!, $paused: Boolean!) {
        data: setPublicEventJudgePaused(mainEventId: $mainEventId, userId: $userId, paused: $paused)
      }
    `,
    { mainEventId, userId, paused }
  );

export const assignPublicEventJudge = (
  user: UserWithToken,
  tournamentId: number,
  userId: number
) =>
  getGraphQLQueryClient(user).request<WithData<{ warning: string | null }>>(
    gql`
      mutation assignPublicEventJudge($tournamentId: Int!, $userId: Int!) {
        data: assignPublicEventJudge(tournamentId: $tournamentId, userId: $userId) {
          warning
        }
      }
    `,
    { tournamentId, userId }
  );

export const markPublicEventNotRunned = (user: UserWithToken, tournamentId: number) =>
  getGraphQLQueryClient(user).request<WithData<boolean>>(
    gql`
      mutation markPublicEventNotRunned($tournamentId: Int!) {
        data: markPublicEventNotRunned(tournamentId: $tournamentId)
      }
    `,
    { tournamentId }
  );

export const markPublicEventPrizesDelivered = (user: UserWithToken, tournamentId: number) =>
  getGraphQLQueryClient(user).request<WithData<boolean>>(
    gql`
      mutation markPublicEventPrizesDelivered($tournamentId: Int!) {
        data: markPublicEventPrizesDelivered(tournamentId: $tournamentId)
      }
    `,
    { tournamentId }
  );

// Plantillas de catálogo (sin sesión). Las usa la app del lead de public
// events, que copia los tipos a su Evento en Firestore al crearlo.

export type PublicEventCatalogTemplate = { key: string; title: string; asOf: string };

export type PublicEventCatalogTemplateType = {
  name: string;
  mode: PublicEventMode;
  formatSlug: string | null;
  tournamentType: TournamentType;
  entry: string | null;
};

export type PublicEventCatalogTemplateDetail = PublicEventCatalogTemplate & {
  types: PublicEventCatalogTemplateType[];
};

export const getPublicEventCatalogTemplates = () =>
  getGraphQLQueryClient(null).request<WithData<PublicEventCatalogTemplate[]>>(
    gql`
      query publicEventCatalogTemplates {
        data: publicEventCatalogTemplates {
          key
          title
          asOf
        }
      }
    `
  );

export const getPublicEventCatalogTemplate = (key: string) =>
  getGraphQLQueryClient(null).request<WithData<Nullable<PublicEventCatalogTemplateDetail>>>(
    gql`
      query publicEventCatalogTemplate($key: String!) {
        data: publicEventCatalogTemplate(key: $key) {
          key
          title
          asOf
          types {
            name
            mode
            formatSlug
            tournamentType
            entry
          }
        }
      }
    `,
    { key }
  );
